// Tenantra Inc. - Register Page (Warm Sand Theme - Microsoft Style)
import { useState, useRef } from 'react';
import { Eye, EyeOff, AlertCircle, CheckCircle, User, Briefcase, ArrowLeft, Mail, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Logo';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { register } from '@/services/auth';
import api from '@/services/api';
import { track } from '@/services/analytics';
import { PROVINCES, formatPostalCode, isValidPostalCode } from '@/constants/provinces';
import ReCAPTCHA from 'react-google-recaptcha';
import { toast } from 'sonner';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    legal_name: '',
    phone: '',
    province: '',
    postal_code: '',
    role_type: 'owner',
    business_name: '',
    language_preference: 'en'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: email/password, 2: email verification, 3: profile details
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const recaptchaRef = useRef(null);
  
  // Email verification state
  const [emailVerified, setEmailVerified] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const passwordRequirements = [
    { label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
    { label: 'One number', test: (p) => /[0-9]/.test(p) },
    { label: 'One special character', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) }
  ];

  const isPasswordValid = passwordRequirements.every(req => req.test(formData.password));
  const doPasswordsMatch = formData.password === formData.confirmPassword && formData.confirmPassword.length > 0;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.startsWith('1')) {
      return '+' + digits.slice(0, 11);
    } else if (digits.length > 0) {
      return '+1' + digits.slice(0, 10);
    }
    return '';
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (!isPasswordValid) {
      setError('Password does not meet requirements');
      return;
    }
    if (!doPasswordsMatch) {
      setError('Passwords do not match');
      return;
    }
    
    // Send email verification OTP
    setSendingOtp(true);
    setError('');
    
    try {
      await api.post('/auth/send-email-otp', { email: formData.email });
      toast.success('Verification code sent to your email');
      setStep(2);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send verification code');
    } finally {
      setSendingOtp(false);
    }
  };
  
  const handleResendOtp = async () => {
    setSendingOtp(true);
    setError('');
    
    try {
      await api.post('/auth/send-email-otp', { email: formData.email });
      toast.success('New verification code sent');
      setOtpCode('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to resend code');
    } finally {
      setSendingOtp(false);
    }
  };
  
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setVerifyingOtp(true);
    setError('');
    
    try {
      await api.post('/auth/verify-email-otp', { 
        email: formData.email, 
        code: otpCode 
      });
      setEmailVerified(true);
      track('register_email_verified');
      toast.success('Email verified successfully!');
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid verification code');
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleStep3Submit = async (e) => {
    e.preventDefault();
    
    // Ensure email is verified
    if (!emailVerified) {
      setError('Please verify your email first');
      setStep(2);
      return;
    }
    if (!formData.province) {
      setError('Select your province or territory');
      return;
    }
    if (!isValidPostalCode(formData.postal_code)) {
      setError('Enter a valid Canadian postal code (e.g. M5V 3L9)');
      return;
    }
    
    setError('');
    setLoading(true);

    try {
      // Try to get reCAPTCHA token, but don't block registration if it fails
      let recaptchaToken = null;
      if (recaptchaRef.current && RECAPTCHA_SITE_KEY) {
        try {
          recaptchaToken = await Promise.race([
            recaptchaRef.current.executeAsync(),
            new Promise((_, reject) => setTimeout(() => reject(new Error('reCAPTCHA timeout')), 1500))
          ]);
          recaptchaRef.current.reset();
        } catch (recaptchaError) {
          console.warn('reCAPTCHA failed, proceeding without it:', recaptchaError);
          // Continue without reCAPTCHA token
        }
      }
      
      await register({
        email: formData.email,
        password: formData.password,
        legal_name: formData.legal_name,
        phone: formData.phone,
        province: formData.province,
        postal_code: formData.postal_code,
        role_type: formData.role_type,
        business_name: formData.business_name || null,
        language_preference: formData.language_preference,
        recaptcha_token: recaptchaToken,
        email_verified: true,
        marketing_opt_in: marketingOptIn,
        sms_opt_in: smsOptIn
      });
      track('register_completed', { role_type: formData.role_type });
      window.location.href = '/landlord/verify-phone';
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6] flex flex-col">
      <header className="h-16 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto h-full px-4 sm:px-6 flex items-center">
          <Logo size="md" data-testid="auth-logo" />
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 sm:px-6 py-10 sm:py-14">
        <div className="w-full max-w-[440px]">
          {/* Progress indicator - Compact */}
          <div className="flex items-center gap-1.5 mb-5" data-testid="register-step-track">
            {['Account', 'Verify email', 'Details'].map((label, i) => {
              const n = i + 1;
              const reached = step >= n;
              const done = step > n;
              return (
                <div key={label} className="flex items-center gap-1.5 flex-1 last:flex-none">
                  <div className="flex items-center gap-2">
                    <div className={`flex items-center justify-center w-7 h-7 rounded-full font-bold text-xs transition-colors duration-300 ${
                      done ? 'bg-emerald-600 text-white' : reached ? 'bg-teal-600 text-white' : 'bg-stone-200 text-stone-400'
                    }`}>{done ? <CheckCircle className="w-3.5 h-3.5" /> : n}</div>
                    <span className={`hidden sm:block text-[11px] font-medium whitespace-nowrap ${step === n ? 'text-stone-800' : 'text-stone-400'}`}>{label}</span>
                  </div>
                  {n < 3 && <div className={`flex-1 h-0.5 rounded-full transition-colors duration-300 ${step > n ? 'bg-teal-600' : 'bg-stone-200'}`} />}
                </div>
              );
            })}
          </div>

          {/* Form container - Compact */}
          <div className="bg-white border border-slate-200 rounded-lg p-6 sm:p-8 shadow-sm">
            <div className="mb-4">
              <h2 className="text-xl font-bold text-stone-900">
                {step === 1 ? 'Create account' : step === 2 ? 'Verify email' : 'Your details'}
              </h2>
            </div>

            {error && (
              <Alert className="mb-4 bg-red-50 border-red-200 text-red-600 py-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-sm">{error}</AlertDescription>
              </Alert>
            )}

            {step === 1 ? (
              <form onSubmit={handleStep1Submit} className="space-y-3.5">
                <div className="space-y-1.5">
                  <Label className="text-stone-700 text-sm">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    required
                    className="h-10 bg-white border-stone-300 text-stone-900 placeholder:text-stone-400 focus:border-teal-600 focus:ring-teal-600/20 text-sm"
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-stone-700 text-sm">Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={(e) => handleChange('password', e.target.value)}
                      required
                      className="h-10 bg-white border-stone-300 text-stone-900 placeholder:text-stone-400 focus:border-teal-600 focus:ring-teal-600/20 pr-10 text-sm"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  
                  {formData.password && (
                    <div className="grid grid-cols-2 gap-1.5 mt-2">
                      {passwordRequirements.map((req, i) => (
                        <div key={i} className="flex items-center gap-1.5">
                          <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center ${
                            req.test(formData.password) ? 'bg-emerald-500' : 'bg-stone-200'
                          }`}>
                            {req.test(formData.password) && <CheckCircle className="w-2.5 h-2.5 text-white" />}
                          </div>
                          <span className={`text-[11px] ${req.test(formData.password) ? 'text-emerald-600' : 'text-stone-400'}`}>
                            {req.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-1.5">
                  <Label className="text-stone-700 text-sm">Confirm Password</Label>
                  <Input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    required
                    className="h-10 bg-white border-stone-300 text-stone-900 placeholder:text-stone-400 focus:border-teal-600 focus:ring-teal-600/20 text-sm"
                    data-testid="register-confirm-password-input"
                  />
                  {formData.confirmPassword && (
                    <div className="flex items-center gap-1.5 mt-1">
                      {doPasswordsMatch ? (
                        <>
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="text-[11px] text-emerald-600">Passwords match</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle className="w-3.5 h-3.5 text-red-500" />
                          <span className="text-[11px] text-red-500">Passwords do not match</span>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Terms & Privacy checkbox */}
                <div className="flex items-start gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-stone-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="terms-checkbox" className="text-xs text-stone-500 leading-relaxed cursor-pointer">
                    I agree to the <a href="/terms-of-service" target="_blank" className="text-teal-600 underline hover:text-teal-700">Terms of Service</a> and <a href="/privacy-policy" target="_blank" className="text-teal-600 underline hover:text-teal-700">Privacy Policy</a>.
                  </label>
                </div>

                {/* Marketing opt-in (optional) */}
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="marketing-checkbox"
                    checked={marketingOptIn}
                    onChange={(e) => setMarketingOptIn(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-stone-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                    data-testid="marketing-checkbox"
                  />
                  <label htmlFor="marketing-checkbox" className="text-xs text-stone-500 leading-relaxed cursor-pointer">
                    Get emails from Tenantra about product updates, rental industry news, and events. You can <a href="/unsubscribe" target="_blank" className="text-teal-600 underline hover:text-teal-700">unsubscribe</a> at any time. <a href="/privacy-policy" target="_blank" className="text-teal-600 underline hover:text-teal-700">Privacy Policy</a>
                  </label>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-9 bg-teal-600 hover:bg-teal-700 text-white font-medium text-sm border-0 transition-colors duration-200"
                  data-testid="register-step1-btn"
                  disabled={sendingOtp || !termsAccepted}
                >
                  {sendingOtp ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending Code...
                    </span>
                  ) : (
                    'Continue'
                  )}
                </Button>
              </form>
            ) : step === 2 ? (
              /* Step 2: Email Verification - Compact */
              <div className="space-y-4">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-teal-50 flex items-center justify-center mx-auto mb-3">
                    <Mail className="w-6 h-6 text-teal-600" />
                  </div>
                  <p className="text-sm text-stone-500">
                    We've sent a 6-digit code to<br />
                    <strong className="text-stone-700">{formData.email}</strong>
                  </p>
                </div>

                <form onSubmit={handleVerifyOtp} className="space-y-3">
                  <div>
                    <Label className="text-stone-700 text-sm">Enter 6-digit code</Label>
                    <Input
                      type="text"
                      value={otpCode}
                      onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="mt-1 text-center text-xl font-mono tracking-widest h-11 bg-white border-stone-200 text-stone-800"
                      maxLength={6}
                      required
                      data-testid="register-otp-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={otpCode.length < 6 || verifyingOtp}
                    className="w-full h-9 bg-teal-600 hover:bg-teal-700 text-white font-medium text-sm border-0 transition-colors duration-200"
                    data-testid="register-verify-otp-btn"
                  >
                    {verifyingOtp ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Verifying...
                      </span>
                    ) : (
                      'Verify Email'
                    )}
                  </Button>
                </form>

                <div className="text-center">
                  <button
                    onClick={handleResendOtp}
                    disabled={sendingOtp}
                    className="text-xs text-teal-600 hover:text-teal-700 flex items-center gap-1.5 mx-auto"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${sendingOtp ? 'animate-spin' : ''}`} />
                    {sendingOtp ? 'Sending...' : "Didn't receive a code? Resend"}
                  </button>
                </div>

                <div className="pt-3 border-t border-stone-200">
                  <button
                    onClick={() => { setStep(1); setError(''); }}
                    className="w-full flex items-center justify-center gap-1.5 text-sm text-stone-500 hover:text-stone-700"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    Back to change email
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleStep3Submit} className="space-y-3.5">
                {/* Verified email badge */}
                <div className="flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span className="text-emerald-700 text-xs font-medium truncate">Email verified: {formData.email}</span>
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-stone-700 text-sm">I am a</Label>
                  <Select value={formData.role_type} onValueChange={(v) => handleChange('role_type', v)}>
                    <SelectTrigger className="h-9 bg-white border-stone-200 text-stone-800 text-sm" data-testid="register-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-stone-200">
                      <SelectItem value="owner" className="text-stone-800 text-sm">
                        <div className="flex items-center gap-2">
                          <User className="w-3.5 h-3.5 text-teal-600" />
                          Property Owner
                        </div>
                      </SelectItem>
                      <SelectItem value="property_manager" className="text-stone-800 text-sm">
                        <div className="flex items-center gap-2">
                          <Briefcase className="w-3.5 h-3.5 text-teal-600" />
                          Property Manager / Agent
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-stone-700 text-sm">Legal Full Name</Label>
                  <Input
                    type="text"
                    value={formData.legal_name}
                    onChange={(e) => handleChange('legal_name', e.target.value)}
                    required
                    className="h-10 bg-white border-stone-300 text-stone-900 placeholder:text-stone-400 focus:border-teal-600 focus:ring-teal-600/20 text-sm"
                    data-testid="register-legal-name-input"
                  />
                </div>

                {formData.role_type === 'property_manager' && (
                  <div className="space-y-1.5">
                    <Label className="text-stone-700 text-sm">Business Name</Label>
                    <Input
                      type="text"
                      value={formData.business_name}
                      onChange={(e) => handleChange('business_name', e.target.value)}
                      className="h-10 bg-white border-stone-300 text-stone-900 placeholder:text-stone-400 focus:border-teal-600 focus:ring-teal-600/20 text-sm"
                      data-testid="register-business-name-input"
                    />
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="text-stone-700 text-sm">Phone Number</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', formatPhoneNumber(e.target.value))}
                    required
                    className="h-10 bg-white border-stone-300 text-stone-900 placeholder:text-stone-400 focus:border-teal-600 focus:ring-teal-600/20 text-sm"
                    data-testid="register-phone-input"
                  />
                  <div className="flex items-start gap-2 pt-1.5">
                    <input
                      type="checkbox"
                      id="sms-consent-checkbox"
                      checked={smsOptIn}
                      onChange={(e) => setSmsOptIn(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border-stone-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                      data-testid="sms-consent-checkbox"
                    />
                    <label htmlFor="sms-consent-checkbox" className="text-[11px] text-stone-500 leading-relaxed cursor-pointer" data-testid="sms-consent-text">
                      (Optional) I agree to receive account notification text messages from Tenantra Inc., including verification codes, rent reminders, payment confirmations, and lease alerts, at the number provided. Message frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of signup. See our <a href="/privacy-policy#sms-messaging" target="_blank" className="text-teal-600 underline hover:text-teal-700">Privacy Policy</a> and <a href="/terms-of-service#sms-terms" target="_blank" className="text-teal-600 underline hover:text-teal-700">Terms</a>.
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-5 gap-3">
                  <div className="space-y-1.5 col-span-3">
                    <Label className="text-stone-700 text-sm">Province / Territory</Label>
                    <Select value={formData.province} onValueChange={(v) => handleChange('province', v)}>
                      <SelectTrigger className="h-10 bg-white border-stone-300 text-stone-800 text-sm" data-testid="register-province-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-stone-200 max-h-64">
                        {PROVINCES.map(([code, name]) => (
                          <SelectItem key={code} value={code} className="text-stone-800 text-sm" data-testid={`register-province-${code}`}>{name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5 col-span-2">
                    <Label className="text-stone-700 text-sm">Postal Code</Label>
                    <Input
                      type="text"
                      value={formData.postal_code}
                      onChange={(e) => handleChange('postal_code', formatPostalCode(e.target.value))}
                      required
                      placeholder="M5V 3L9"
                      className="h-10 bg-white border-stone-300 text-stone-900 placeholder:text-stone-400 focus:border-teal-600 focus:ring-teal-600/20 text-sm font-mono"
                      data-testid="register-postal-code-input"
                    />
                  </div>
                </div>
                <p className="text-[11px] text-stone-500 -mt-2">Used to apply the correct sales tax to your subscription.</p>

                <div className="space-y-1.5">
                  <Label className="text-stone-700 text-sm">Preferred Language</Label>
                  <Select value={formData.language_preference} onValueChange={(v) => handleChange('language_preference', v)}>
                    <SelectTrigger className="h-9 bg-white border-stone-200 text-stone-800 text-sm" data-testid="register-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-stone-200">
                      <SelectItem value="en" className="text-stone-800 text-sm">English</SelectItem>
                      <SelectItem value="fr" className="text-stone-800 text-sm">Français</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Invisible reCAPTCHA */}
                {RECAPTCHA_SITE_KEY && (
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                    size="invisible"
                    badge="bottomright"
                  />
                )}

                <div className="flex gap-2.5 pt-1">
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={() => setStep(1)} 
                    className="flex-1 h-9 border-stone-200 bg-white hover:bg-stone-50 text-stone-700 text-sm"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1.5" />
                    Back
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1 h-9 bg-teal-600 hover:bg-teal-700 text-white font-medium text-sm border-0 transition-colors duration-200"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Creating...
                      </span>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </div>
              </form>
            )}

            <div className="mt-4 pt-4 border-t border-stone-200">
              <p className="text-center text-sm text-stone-500">
                Already have an account?{' '}
                <a href="/login" className="text-teal-600 hover:text-teal-700 font-medium transition-colors">
                  Sign in
                </a>
              </p>
            </div>
          </div>

          {/* Tenant link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Looking for your Rent Report?{' '}
              <a href="/tenant/access" className="text-teal-700 hover:text-teal-800 font-medium transition-colors">
                Go to Tenant Portal
              </a>
            </p>
          </div>
        </div>
      </main>

      <footer className="py-6 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Tenantra Inc. ·{' '}
        <a href="/privacy-policy" className="hover:text-slate-700">Privacy</a> ·{' '}
        <a href="/terms-of-service" className="hover:text-slate-700">Terms</a>
      </footer>
    </div>
  );
}
